/**
 * PreCaseMinderHistoryPanel — BM-H4320.
 *
 * Vanessa, 14 Oct 2026: a matter opened before CaseMinder and registered on the
 * Pre-CaseMinder form still has to pass the golden rule, and "we should be able
 * to confirm dates to pass it". Staff record, for each step, the date it
 * was done and where the evidence is held. Each one feeds the same check the
 * live chain uses; nothing is re-run and nothing goes to the client. Any member
 * of staff may record them (Vanessa, 15 Oct 2026: "don't gate it to a director").
 *
 * Shown only on a matter with a Pre-CaseMinder registration.
 *
 * BM-H4541 (Vanessa, 20 Oct 2026: "Can it look in the SharePoint folder for
 * compliance items?"): "Look in the matter folder" lists the files in the
 * matter's own folder that look like evidence of each step. Picking one fills
 * in where the evidence is held. Nothing is ticked: the person still gives the
 * date and records the step.
 *
 * BM-H4608 (Vanessa, 22 Oct 2026: "pre-CM registration doesn't kick off a
 * whole pipeline that's really already satisfied by pre-CM actions ... it
 * checks folders for what's already there so it can simply be confirmed by
 * the user"): the panel looks in the folder as soon as it opens and fills in
 * each outstanding step from the newest matching file, its saved date as the
 * date. Nothing is recorded until the person checks the rows and confirms.
 *
 * BM-H4878 (Vanessa, 29 Oct 2026): matters opened while CaseMinder was being
 * fixed went ahead by hand and their steps were never recorded. "i don't want
 * to have to go trawling through sharepoint": a step CaseMinder found in the
 * folder is confirmed as it stands. A step it could not find is marked so,
 * links to the compliance step itself to put it right, and needs "a mini risk
 * assessment or reasons recorded and by whom". The date is optional (a step
 * with no date is a remediation too), and the conflict check may take the
 * file opening date. Nothing here starts a conflict check or mints a number.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, FolderSearch, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { currentUserEmail } from "../utils/currentUserEmail";
import { openMatterCcl } from "../utils/openMatterCcl";
import { PRE_CM_CONFLICT_NOTE, folderState, remediationLink } from "../utils/preCmRemediation";

const EMPTY = {
  date: "", evidence: "", risk_band: "", sof_evidence: "", fromFolder: false,
  remediation: "", before_work_started: "", date_basis: "",
};

// Mirrors hub/pre_cm_history.py (BM-H4878). The server is authoritative.
const REMEDIATION_MIN_CHARS = 40;
const BEFORE_WORK_STEPS = new Set(["aml", "ccl"]);
const fromFolderEvidence = (evidence) => (evidence || "").startsWith("SharePoint: ");
// A step needs reasons unless its evidence is a file found in the folder and
// it has a date.
export const needsReasons = (row) => !fromFolderEvidence(row.evidence) || !row.date;
const touched = (row) => !!(row && (row.date || (row.evidence || "").trim() || (row.remediation || "").trim()));

const EVIDENCE_HINT = {
  conflict: "Where the conflict search is recorded",
  ob: "Client instruction form, where it is saved",
  aml: "ID documents held, where they are saved",
  forf_ra: "File opening form or risk assessment, where it is saved",
  ccl: "Signed engagement letter, where it is saved",
};

export default function PreCaseMinderHistoryPanel({ matterRef, onChange }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null);
  const [looking, setLooking] = useState(false);

  const url = matterRef ? `${API}/pipeline/pre-caseminder/${encodeURIComponent(matterRef)}/history` : "";

  const load = useCallback(async () => {
    if (!url) return;
    try {
      const response = await api.get(url);
      setState(response.data || null);
    } catch (error) {
      // A 404 is a backend without this route yet (the frontend goes live on
      // merge, the backend at the swap). The panel stays hidden.
      if (error?.response?.status !== 404) {
        toast.error(errorMessage(error, "Could not load the Pre-CaseMinder steps"));
      }
      setState(null);
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  const lookInFolder = async () => {
    try {
      setLooking(true);
      const response = await api.get(
        `${API}/pipeline/pre-caseminder/${encodeURIComponent(matterRef)}/folder-evidence`);
      setFound(response.data || null);
    } catch (error) {
      toast.error(errorMessage(error, "Could not look in the matter folder"));
    } finally {
      setLooking(false);
    }
  };

  const outstandingKeys = (state?.steps || []).filter((s) => !s.on_record).map((s) => s.key).join(",");

  // BM-H4608: look in the folder without being asked, once, when there is
  // something left to record.
  useEffect(() => {
    if (state?.eligible && outstandingKeys && found === null && !looking) lookInFolder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.eligible, outstandingKeys]);

  // BM-H4608: fill each outstanding step from the newest matching file. A row
  // the person has already touched is left alone.
  useEffect(() => {
    if (!found?.available || !outstandingKeys) return;
    setForm((f) => {
      const next = { ...f };
      for (const key of outstandingKeys.split(",")) {
        const best = (found.suggestions?.[key] || [])[0];
        if (!best || next[key]) continue;
        next[key] = {
          ...EMPTY,
          date: best.modified ? String(best.modified).slice(0, 10) : "",
          evidence: `SharePoint: ${best.path}`,
          fromFolder: true,
        };
      }
      return next;
    });
  }, [found, outstandingKeys]);

  if (!state?.eligible) return null;

  const outstanding = state.steps.filter((s) => !s.on_record);
  const set = (key, field, value) =>
    setForm((f) => ({ ...f, [key]: { ...EMPTY, ...f[key], [field]: value } }));
  const setFields = (key, fields) =>
    setForm((f) => ({ ...f, [key]: { ...EMPTY, ...f[key], ...fields } }));
  // The panel is also mounted outside a router (its tests), so it leaves the
  // page with a plain navigation rather than useNavigate.
  const go = (path) => window.location.assign(path);
  const openRemediation = (link) => {
    if (link.ccl) openMatterCcl({ matterRef, axios: api, apiBase: API, navigate: go });
    else go(link.path);
  };
  const me = currentUserEmail();
  const pickFile = (key, f) =>
    setForm((prev) => ({
      ...prev,
      [key]: {
        ...EMPTY,
        ...prev[key],
        evidence: `SharePoint: ${f.path}`,
        date: f.modified ? String(f.modified).slice(0, 10) : (prev[key]?.date || ""),
      },
    }));
  const anyFromFolder = outstanding.some((s) => form[s.key]?.fromFolder);

  const submit = async () => {
    // The filled rows, in chain order. The server refuses a gap.
    const steps = {};
    for (const s of outstanding) {
      const row = form[s.key];
      if (!touched(row)) break;
      if (needsReasons(row) && (row.remediation || "").trim().length < REMEDIATION_MIN_CHARS) {
        toast.error(`"${s.label}": record the reasons or a short risk assessment (at least ${REMEDIATION_MIN_CHARS} characters).`);
        return;
      }
      steps[s.key] = {
        date: row.date || undefined,
        evidence: (row.evidence || "").trim() || undefined,
        ...(row.date_basis ? { date_basis: row.date_basis } : {}),
        ...(needsReasons(row) ? {
          remediation: row.remediation.trim(),
          before_work_started: BEFORE_WORK_STEPS.has(s.key) ? (row.before_work_started || undefined) : undefined,
        } : {}),
        ...(s.key === "forf_ra" ? {
          risk_band: row.risk_band || undefined,
          sof_evidence: (row.sof_evidence || "").trim() || undefined,
        } : {}),
      };
    }
    if (!Object.keys(steps).length) {
      toast.error(`Fill in "${outstanding[0]?.label}" first: every step gates the next.`);
      return;
    }
    try {
      setBusy(true);
      const response = await api.post(url, { steps });
      setState(response.data || null);
      setForm({});
      if (onChange) onChange();
      toast.success(response.data?.complete
        ? "Every golden rule step is on record. The matter has passed the chain."
        : `${Object.keys(steps).length} step(s) recorded.`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not record the steps"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="pre-cm-history" className="mt-6 rounded-lg overflow-hidden border border-white/10" data-testid="pre-cm-history-panel">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-white bg-white/[0.04] px-4 py-2">
        <History className="h-4 w-4" /> Steps done before CaseMinder
      </h2>
      <div className="p-4 text-xs text-slate-200">
        <ul className="space-y-1" data-testid="pre-cm-history-steps">
          {state.steps.filter((s) => s.on_record).map((s) => (
            <li key={s.key} className="flex items-start gap-2" data-testid={`pre-cm-step-done-${s.key}`}>
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
              <span>
                {s.label}
                {s.confirmed
                  ? `: ${s.confirmed.date}, ${s.confirmed.evidence}. Confirmed by ${s.confirmed.confirmed_by}.`
                  : ": on record in CaseMinder."}
              </span>
            </li>
          ))}
        </ul>

        {outstanding.length > 0 && (
          <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3 text-slate-800" data-testid="pre-cm-history-form">
            <p className="mb-2 font-semibold" data-testid="pre-cm-history-intro">
              {anyFromFolder
                ? "These were found in the matter folder. Check each date and file, change anything that is wrong, then confirm."
                : "Record when each step was done and where the evidence is held."}
            </p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={lookInFolder}
                disabled={looking}
                className="inline-flex items-center gap-1.5 rounded border border-teal px-2.5 py-1.5 font-medium text-teal hover:bg-teal/10 disabled:opacity-50"
                data-testid="pre-cm-look-in-folder"
              >
                {looking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderSearch className="h-3.5 w-3.5" />}
                {found ? "Look again" : "Look in the matter folder"}
              </button>
              {found && (
                <span className="text-[11px] text-slate-600" data-testid="pre-cm-folder-summary">
                  {found.available
                    ? `Looked at ${found.files_examined} file(s) in ${found.folder_name}.`
                      + (found.truncated ? " Some folders could not be read, so this list may be short." : "")
                      + " The date filled in is the day the file was saved; change it if the step was done on another day."
                    : found.reason}
                </span>
              )}
            </div>
            {outstanding.map((s) => {
              const row = { ...EMPTY, ...form[s.key] };
              const inFolder = folderState(found, s.key);
              const link = remediationLink(s.key, matterRef);
              return (
                <div key={s.key} className="mb-3 border-b border-slate-200 pb-3 last:mb-0 last:border-0 last:pb-0"
                     data-testid={`pre-cm-step-${s.key}`}>
                  <p className="font-medium">
                    {s.label}
                    {row.fromFolder && (
                      <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800"
                            data-testid={`pre-cm-from-folder-${s.key}`}>
                        Found in folder: check
                      </span>
                    )}
                    {inFolder === "missing" && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                            data-testid={`pre-cm-missing-${s.key}`}>
                        <AlertTriangle className="h-3 w-3" /> Not found in SharePoint
                      </span>
                    )}
                  </p>
                  <div className="mt-1 grid gap-2 sm:grid-cols-[9rem_1fr]">
                    <input
                      type="date"
                      value={row.date}
                      onChange={(e) => setFields(s.key, { date: e.target.value, date_basis: "" })}
                      className="rounded border border-slate-300 bg-white p-1.5"
                      aria-label={`${s.label}: date (leave blank if not known)`}
                      title="Leave blank if not known: it is then recorded as a remediation"
                      data-testid={`pre-cm-date-${s.key}`}
                    />
                    <input
                      type="text"
                      value={row.evidence}
                      onChange={(e) => set(s.key, "evidence", e.target.value)}
                      placeholder={EVIDENCE_HINT[s.key]}
                      className="rounded border border-slate-300 bg-white p-1.5"
                      aria-label={`${s.label}: evidence`}
                      data-testid={`pre-cm-evidence-${s.key}`}
                    />
                  </div>
                  {found?.available && (
                    <div className="mt-1" data-testid={`pre-cm-found-${s.key}`}>
                      {(found.suggestions?.[s.key] || []).length === 0 ? (
                        <p className="text-[11px] text-slate-500">Nothing in the folder looks like this step.</p>
                      ) : (
                        <ul className="space-y-0.5">
                          {found.suggestions[s.key].map((f) => {
                            // "Use" puts this file (and the day it was saved)
                            // into the boxes above; the file already in them
                            // shows as "In use". Nothing is recorded until the
                            // Confirm button below (Vanessa, 29 Oct 2026: the
                            // buttons seemed to do nothing).
                            const inUse = row.evidence === `SharePoint: ${f.path}`;
                            return (
                            <li key={f.path} className="flex flex-wrap items-center gap-2 text-[11px]">
                              <button
                                type="button"
                                onClick={() => pickFile(s.key, f)}
                                disabled={inUse}
                                title={inUse ? "This file is in the boxes above" : "Put this file and its date in the boxes above"}
                                className={inUse
                                  ? "inline-flex items-center gap-1 rounded border border-emerald-500 bg-emerald-50 px-1.5 py-0.5 text-emerald-700"
                                  : "rounded border border-slate-300 bg-white px-1.5 py-0.5 hover:border-teal"}
                                data-testid={`pre-cm-use-${s.key}`}
                              >
                                {inUse ? <><CheckCircle2 className="h-3 w-3" /> In use</> : "Use"}
                              </button>
                              {f.web_url
                                ? <a href={f.web_url} target="_blank" rel="noreferrer" className="text-teal underline">{f.path}</a>
                                : <span>{f.path}</span>}
                              {f.modified && <span className="text-slate-500">saved {String(f.modified).slice(0, 10)}</span>}
                            </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                  {s.key === "conflict" && found?.file_opening_date && (
                    <button
                      type="button"
                      onClick={() => setFields(s.key, { date: found.file_opening_date, date_basis: "file_opening" })}
                      disabled={row.date_basis === "file_opening"}
                      className="mt-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] hover:border-teal disabled:border-emerald-500 disabled:text-emerald-700"
                      data-testid="pre-cm-use-file-opening"
                    >
                      {row.date_basis === "file_opening"
                        ? `Using the file opening date, ${found.file_opening_date}`
                        : `Use the file opening date (${found.file_opening_date})`}
                    </button>
                  )}
                  {needsReasons(row) && (
                    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2" data-testid={`pre-cm-remediation-${s.key}`}>
                      <p className="mb-1 text-[11px] text-amber-900">
                        {!row.date && fromFolderEvidence(row.evidence)
                          ? "No date given, so this is recorded as a remediation."
                          : "Not backed by a file CaseMinder found in the matter folder, so this is recorded as a remediation."}
                        {" "}Record the reasons or a short risk assessment
                        {me ? `. It is recorded as given by ${me}.` : "."}
                      </p>
                      {link ? (
                        <button
                          type="button"
                          onClick={() => openRemediation(link)}
                          className="mb-1 text-[11px] font-medium text-teal underline"
                          data-testid={`pre-cm-remediate-link-${s.key}`}
                        >
                          {link.label} to put it right →
                        </button>
                      ) : (
                        <p className="mb-1 text-[11px] font-medium text-amber-900" data-testid={`pre-cm-remediate-note-${s.key}`}>
                          {PRE_CM_CONFLICT_NOTE}
                        </p>
                      )}
                      <textarea
                        value={row.remediation}
                        onChange={(e) => set(s.key, "remediation", e.target.value)}
                        rows={2}
                        placeholder="Why the record is missing, what was done instead, and the risk"
                        className="w-full rounded border border-slate-300 bg-white p-1.5"
                        aria-label={`${s.label}: reasons or risk assessment`}
                        data-testid={`pre-cm-reasons-${s.key}`}
                      />
                      {BEFORE_WORK_STEPS.has(s.key) && (
                        <select
                          value={row.before_work_started}
                          onChange={(e) => set(s.key, "before_work_started", e.target.value)}
                          className="mt-1 rounded border border-slate-300 bg-white p-1.5"
                          aria-label={`${s.label}: done before work started?`}
                          data-testid={`pre-cm-before-work-${s.key}`}
                        >
                          <option value="">Done before work on the matter started?</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="not_known">Not known</option>
                        </select>
                      )}
                    </div>
                  )}
                  {s.key === "forf_ra" && (
                    <div className="mt-2 grid gap-2 sm:grid-cols-[9rem_1fr]">
                      <select
                        value={row.risk_band}
                        onChange={(e) => set(s.key, "risk_band", e.target.value)}
                        className="rounded border border-slate-300 bg-white p-1.5"
                        aria-label="Risk rating"
                        data-testid="pre-cm-risk-band"
                      >
                        <option value="">Risk rating</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      {state.sof_applies && (
                        <input
                          type="text"
                          value={row.sof_evidence}
                          onChange={(e) => set(s.key, "sof_evidence", e.target.value)}
                          placeholder="How the source of funds was evidenced"
                          className="rounded border border-slate-300 bg-white p-1.5"
                          aria-label="Source of funds evidence"
                          data-testid="pre-cm-sof-evidence"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <p className="mt-2 text-[11px] text-slate-500">
              Give the date each step actually happened, in whatever order they were done. A file
              CaseMinder found in the folder is checked there again when you confirm. Nothing is sent
              to the client and no new matter number is opened.
            </p>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-1.5 rounded bg-teal px-2.5 py-1.5 font-medium text-white hover:bg-teal/90 disabled:opacity-50"
              data-testid="pre-cm-history-save"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {anyFromFolder ? "Confirm these steps" : "Record these steps"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
